// src/pages/AccountLoginPage.tsx
// Passwordless sign-in: customer enters their email, receives a 6-digit code,
// then enters that code to authenticate.

import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { sendCustomerOtp, verifyCustomerOtp } from "../api/customer";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import PageContainer, { GapSize } from "../components/PageContainer";
import OrderWrapper from "../components/OrderWrapper";
import Spinner from "../components/Spinner";

type Step = "email" | "otp";

const AccountLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoggedIn } = useCustomerAuth();
  const [searchParams] = useSearchParams();

  // Optional post-login destination (?returnTo=/account/orders/<id>), used by
  // deep links like the review-request email. Restricted to same-site paths —
  // a value like "//evil.com" or "https://…" is ignored (open-redirect guard).
  const rawReturnTo = searchParams.get("returnTo");
  const returnTo =
    rawReturnTo && rawReturnTo.startsWith("/") && !rawReturnTo.startsWith("//")
      ? rawReturnTo
      : "/account";

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Redirect if already logged in
  useEffect(() => {
    if (isLoggedIn) navigate(returnTo);
  }, [isLoggedIn, navigate, returnTo]);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const result = await sendCustomerOtp(email.trim().toLowerCase());
      if (!result || result.error || !result.data?.otpToken) {
        setError(result?.error ?? "Failed to send code. Please try again.");
        // Use server-provided retryAfter if rate-limited
        if (
          result &&
          "retryAfter" in result &&
          typeof result.retryAfter === "number"
        ) {
          setResendCooldown(result.retryAfter);
        }
        return;
      }
      setOtpToken(result.data.otpToken);
      setStep("otp");
      setResendCooldown(60);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const result = await verifyCustomerOtp(
        email.trim().toLowerCase(),
        otp.trim(),
        otpToken,
      );
      if (!result || result.error || !result.data?.authToken) {
        setError(result?.error ?? "Verification failed. Please try again.");
        return;
      }
      login(result.data.authToken, result.data.customer);
      navigate(returnTo);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError(null);
    setIsLoading(true);
    try {
      const result = await sendCustomerOtp(email.trim().toLowerCase());
      if (!result || result.error || !result.data?.otpToken) {
        setError(result?.error ?? "Failed to resend code.");
        // Use server-provided retryAfter if rate-limited
        if (
          result &&
          "retryAfter" in result &&
          typeof result.retryAfter === "number"
        ) {
          setResendCooldown(result.retryAfter);
        }
        return;
      }
      setOtpToken(result.data.otpToken);
      setOtp("");
      setResendCooldown(60);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <Spinner text="Signing In..." />}
      <PageContainer hasPadding gapSize={GapSize.Small}>
        <div className="max-w-md mx-auto w-full">
          <OrderWrapper>
            {/* Header */}
            <div className="text-center">
              <h1 className="text-3xl playfair-text text-brown-dark mb-2">
                My Account
              </h1>
              <p className="text-sm text-gray-600 lora-text">
                {step === "email"
                  ? "Enter your email to sign in or create an account."
                  : `We sent a 6-digit code to ${email}.`}
              </p>
            </div>

            <div className="w-full h-px bg-accent-light" />

            {step === "email" ? (
              <form onSubmit={handleSendOtp} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="login-email"
                    className="text-sm font-semibold text-brown-dark opensans-text"
                  >
                    Email Address
                  </label>
                  <input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="order-input-field"
                  />
                </div>

                {error && (
                  <p className="text-rust text-sm text-center">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={isLoading || !email.trim()}
                  className="btn-primary w-full"
                >
                  Send Sign-In Code
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="login-otp"
                    className="text-sm font-semibold text-brown-dark opensans-text"
                  >
                    Verification Code
                  </label>
                  <input
                    id="login-otp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    required
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder="123456"
                    className="order-input-field text-center tracking-[0.5em] text-2xl font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This code expires in 10 minutes.
                  </p>
                </div>

                {error && (
                  <p className="text-rust text-sm text-center">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={isLoading || otp.length < 6}
                  className="btn-primary w-full"
                >
                  Sign In
                </button>

                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setStep("email");
                      setOtp("");
                      setError(null);
                    }}
                    className="text-brown hover:text-brown-dark underline"
                  >
                    ← Change email
                  </button>
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendCooldown > 0 || isLoading}
                    className="text-brown hover:text-brown-dark underline disabled:opacity-50"
                  >
                    {resendCooldown > 0
                      ? `Resend in ${resendCooldown}s`
                      : "Resend code"}
                  </button>
                </div>
              </form>
            )}
          </OrderWrapper>
        </div>
      </PageContainer>
    </>
  );
};

export default AccountLoginPage;
